import styles from './discussions.module.css'

import * as React from 'react'

import { PLAYING } from '@disco/constants/MediaStatus'
import { parseTimestampToSeconds } from '@disco/shared-components/rich-text-editor'
import {
  playTrack,
  seekTrackPosition,
} from '@disco/state-management/actions/MediaActions'
import { selectTrackPlaybackStatus } from '@disco/state-management/selectors/media'
import { useGetDiscussionsQuery } from '@disco/state-management/api/discussions'

import type { DiscussionVisibility } from 'types/discussion'
import type { TrackId } from 'types/track'

import { Box, DiscoCallout, Flex, Spinner, Text } from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'

import CommentEditor from './comment-editor'
import DiscussionComment from './discussion-comment'
import DiscussionVisibilityControl from './visibility-control'

export const DISCUSSION_PAGE_SIZE = 100

type Props = {
  trackId: TrackId
  trackDurationSeconds?: number
  visibility: DiscussionVisibility
  onVisibilityChange: (visibility: DiscussionVisibility) => void
}

const TrackDiscussions = ({
  trackId,
  trackDurationSeconds,
  visibility,
  onVisibilityChange,
}: Props): React.ReactElement => {
  const { data, isLoading, isError } = useGetDiscussionsQuery({
    target_type: 'track',
    target_object_id: trackId,
    visibility,
    count: DISCUSSION_PAGE_SIZE,
  })

  const discussions = data?.results ?? []

  const listAreaRef = React.useRef<HTMLDivElement>(null)

  // Auto-scroll to the newest note the first time a tab is viewed for the
  // current track; on a same-track tab switch (client <-> internal notes)
  // restore whatever scroll position the user left that tab at instead of
  // clobbering it. Navigating to a different track resets this memory so a
  // track's notes still open scrolled to the newest note per SLAM-2182, even
  // if you'd previously scrolled up on it before navigating away. trackId
  // must stay in the effect's deps: the Notes tab isn't remounted when the
  // edit-metadata modal's next/prev track nav swaps trackId in place, and if
  // the new track's discussions are already RTK-Query-cached, isLoading
  // never transitions, so this effect wouldn't otherwise re-run.
  const currentTrackIdRef = React.useRef(trackId)
  const scrollPositionsRef = React.useRef<Map<DiscussionVisibility, number>>(
    new Map(),
  )

  React.useLayoutEffect(() => {
    if (isLoading) return
    const el = listAreaRef.current
    if (!el) return

    if (currentTrackIdRef.current !== trackId) {
      currentTrackIdRef.current = trackId
      scrollPositionsRef.current = new Map()
    }

    el.scrollTop = scrollPositionsRef.current.get(visibility) ?? el.scrollHeight
    scrollPositionsRef.current.set(visibility, el.scrollTop)
  }, [trackId, visibility, isLoading])

  const handleListAreaScroll = React.useCallback(() => {
    const el = listAreaRef.current
    if (!el) return
    scrollPositionsRef.current.set(visibility, el.scrollTop)
  }, [visibility])

  const dispatch = useDispatch()
  const playbackStatus = useSelector(state =>
    selectTrackPlaybackStatus(state, { trackId }),
  )

  const handleTimestampClick = React.useCallback(
    (timestamp: string) => {
      // handles both single timestamp and timestamp range to extract the start token
      const startToken = timestamp.split('-')[0]
      if (!startToken) return
      const seconds = parseTimestampToSeconds(startToken)
      if (!Number.isFinite(seconds)) return

      if (playbackStatus !== PLAYING) {
        dispatch(
          playTrack(trackId, 'in-app', { playbackLocation: 'Track comments' }),
        )
      }
      dispatch(seekTrackPosition(trackId, seconds))
    },
    [dispatch, playbackStatus, trackId],
  )

  return (
    <Box width="100%" className={`${styles.container} TrackDiscussionsRoot`}>
      <DiscussionVisibilityControl
        value={visibility}
        onValueChange={onVisibilityChange}
      />
      {discussions.length === DISCUSSION_PAGE_SIZE && (
        <Box my="4">
          <DiscoCallout type="info">
            {`Notes are currently limited to displaying the last ${DISCUSSION_PAGE_SIZE} comments`}
          </DiscoCallout>
        </Box>
      )}
      <Box
        mt="3"
        className={styles.listArea}
        ref={listAreaRef}
        onScroll={handleListAreaScroll}
        data-testid="notes-list-area"
      >
        {isLoading ? (
          <Flex align="center" gap="2" p="3">
            <Spinner />
            <Text size="2" color="gray">
              Loading notes…
            </Text>
          </Flex>
        ) : isError ? (
          <Text size="2" color="red" as="p">
            Failed to load notes. Please try again.
          </Text>
        ) : discussions.length === 0 ? (
          <Text size="2" color="gray" as="p">
            No notes yet.
          </Text>
        ) : (
          <Flex direction="column" gap="3" className={styles.list}>
            {[...discussions].reverse().map(d => (
              <DiscussionComment
                key={d.id}
                discussion={d}
                trackDurationSeconds={trackDurationSeconds}
                onTimestampClick={handleTimestampClick}
              />
            ))}
          </Flex>
        )}
      </Box>

      <Box pt="3" className={styles.editorWrapper}>
        <CommentEditor
          key={visibility}
          mode="create"
          trackId={trackId}
          visibility={visibility}
          trackDurationSeconds={trackDurationSeconds}
        />
      </Box>
    </Box>
  )
}

export default TrackDiscussions
